import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, mapLimit, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, ChevronUp, ChevronDown, Save } from "lucide-react";

/**
 * AdminSbtSponsors — CRUD for the Sponsor & Exhibitor logo strip on the
 * marketing home page. Add / edit / delete / reorder rows.
 */
export default function AdminSbtSponsors() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sbt-site/sponsors");
      setRows((r.data.sponsors || []).map((s) => ({ ...s, _dirty: false })));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load sponsors");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setField = (id, k, v) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v, _dirty: true } : r)));

  const move = (idx, dir) =>
    setRows((rs) => {
      const target = idx + dir;
      if (target < 0 || target >= rs.length) return rs;
      const copy = [...rs];
      [copy[idx], copy[target]] = [copy[target], copy[idx]];
      return copy.map((r, i) => ({ ...r, position: i + 1, _dirty: true }));
    });

  const addRow = async () => {
    try {
      const r = await api.post("/admin/sbt-site/sponsors", {
        name: "New Sponsor", logo_url: "", link_url: "", position: 0, active: true,
      });
      setRows((rs) => [...rs, { ...r.data, _dirty: false }]);
      toast.success("Row added — fill in logo URL, then Save All.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Add failed"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete sponsor "${name}"?`)) return;
    try {
      await api.delete(`/admin/sbt-site/sponsors/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      // First push reorder (positions from array index)
      await api.post("/admin/sbt-site/sponsors/reorder", { order: rows.map((r) => r.id) });
      // Then push per-row updates for dirty ones (max 4 in flight)
      const dirty = rows.filter((r) => r._dirty);
      await mapLimit(dirty, 4, (r) => api.put(`/admin/sbt-site/sponsors/${r.id}`, {
        name: r.name || "", logo_url: r.logo_url || "", link_url: r.link_url || "",
        position: r.position || 0, active: !!r.active,
      }));
      toast.success(`Saved ${dirty.length || 0} row(s).`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-sponsors">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold tracking-tight">Sponsor &amp; Exhibitor Logos</h3>
          <p className="text-sm text-slate-500 mt-1">Add, remove, reorder the logos shown on the marketing home page.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={addRow} data-testid="add-sponsor-btn"><Plus className="w-4 h-4 mr-1"/> Add Logo</Button>
          <Button size="sm" onClick={saveAll} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-sponsors-btn">
            <Save className="w-4 h-4 mr-1"/> {saving ? "Saving…" : "Save All"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
          No sponsors yet — click <strong>Add Logo</strong>.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={r.id} className="grid grid-cols-12 gap-2 items-center rounded border border-slate-200 bg-white p-3" data-testid={`sponsor-row-${i}`}>
              <div className="col-span-1 flex flex-col items-center gap-1">
                <button title="Up" disabled={i === 0} onClick={() => move(i, -1)} className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronUp className="w-4 h-4"/></button>
                <span className="text-[10px] text-slate-400">{i + 1}</span>
                <button title="Down" disabled={i === rows.length - 1} onClick={() => move(i, +1)} className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronDown className="w-4 h-4"/></button>
              </div>
              <div className="col-span-2 h-16 rounded border border-slate-200 bg-slate-50 flex items-center justify-center p-1">
                {r.logo_url ? <img src={r.logo_url} alt="" className="max-h-14 max-w-full object-contain"/> : <span className="text-[11px] text-slate-400">no logo</span>}
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Name</Label>
                <Input value={r.name || ""} onChange={(e) => setField(r.id, "name", e.target.value)} className="h-8 text-sm" data-testid={`sponsor-name-${i}`}/>
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Logo URL</Label>
                <Input value={r.logo_url || ""} onChange={(e) => setField(r.id, "logo_url", e.target.value)} className="h-8 text-sm" data-testid={`sponsor-logo-${i}`}/>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Link URL</Label>
                <Input value={r.link_url || ""} onChange={(e) => setField(r.id, "link_url", e.target.value)} className="h-8 text-sm" data-testid={`sponsor-link-${i}`}/>
              </div>
              <div className="col-span-1 flex flex-col items-center gap-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Live</Label>
                <Switch checked={!!r.active} onCheckedChange={(v) => setField(r.id, "active", !!v)} data-testid={`sponsor-active-${i}`}/>
              </div>
              <button onClick={() => del(r.id, r.name)} title="Delete" className="col-span-12 justify-self-end text-rose-600 hover:text-rose-800 text-xs flex items-center gap-1" data-testid={`sponsor-delete-${i}`}>
                <Trash2 className="w-3.5 h-3.5"/> Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
